/**
 * @miraki/money — fixed-precision money for the wholesale portal.
 *
 * NF-04 requires fixed-precision decimal end to end. JavaScript has no decimal
 * type and JSON has no decimal type, so the rules below are not style — they are
 * the mechanism that stops `12480.10` silently becoming a float somewhere between
 * Postgres and a retailer's ledger.
 *
 *   1. `Money` is branded. A raw `number` can never be assigned to it.
 *   2. Postgres column type is NUMERIC(19,4), always.
 *   3. postgres-js must return numerics as STRINGS (see apps/api/src/db/client.ts).
 *   4. Money crosses every API boundary as a string, never a number.
 *   5. Arithmetic goes through this module. An ESLint rule bans operators on Money.
 *   6. A property test asserts round-trip fidelity. It runs in CI.
 */
import Decimal from 'decimal.js';

Decimal.set({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -9e15,
  toExpPos: 9e15,
});

declare const MoneyBrand: unique symbol;
/**
 * A fixed-precision monetary amount. Construct only via `money()` or `fromDb()`.
 *
 * Declared as an `interface`, not a type alias, on purpose. As an alias TypeScript
 * expands it to `Decimal & { [MoneyBrand]: 'Money' }` when inferring the type of anything
 * built from it — so every Zod schema in `packages/contracts` that carries a Money field
 * ended up with an inferred type naming `MoneyBrand`, which is not exported, and
 * declaration emit failed with TS4023. An interface is preserved by name, so consumers
 * refer to `Money` and the brand stays private.
 */
export interface Money extends Decimal {
  readonly [MoneyBrand]: 'Money';
}

/** NUMERIC(19,4): up to 15 integer digits, up to 4 fractional. */
export const SCALE = 4;
export const MONEY_PATTERN = /^-?\d{1,15}(\.\d{1,4})?$/;

export class MoneyError extends Error {}

/** Construct from a string. Numbers are rejected by design — rule 1. */
export function money(value: string): Money {
  if (typeof value !== 'string') {
    throw new MoneyError(`Money must be built from a string, received ${typeof value}`);
  }
  const trimmed = value.trim();
  if (!MONEY_PATTERN.test(trimmed)) {
    throw new MoneyError(`"${value}" is not a valid monetary amount`);
  }
  return new Decimal(trimmed) as Money;
}

/** Values arriving from Postgres. Throws loudly if the driver parsed to a number. */
export function fromDb(value: string | null): Money | null {
  if (value === null) return null;
  if (typeof value === 'number') {
    throw new MoneyError(
      'Received a number from the database. postgres-js is parsing NUMERIC to a JS ' +
        'number and precision is already lost. Fix the driver types in db/client.ts.',
    );
  }
  return money(value);
}

/** Always emits exactly SCALE decimal places, for storage and for the wire. */
export function toDb(m: Money): string {
  return m.toFixed(SCALE);
}
export const toJSON = toDb;

export const ZERO: Money = new Decimal('0') as Money;

export function add(a: Money, b: Money): Money { return a.plus(b) as Money; }
export function sub(a: Money, b: Money): Money { return a.minus(b) as Money; }
export function neg(a: Money): Money { return a.negated() as Money; }
export function absolute(a: Money): Money { return a.abs() as Money; }
export function sum(xs: readonly Money[]): Money {
  return xs.reduce<Money>((acc, x) => add(acc, x), ZERO);
}

/** Multiply by a dimensionless scalar — a rate, a percentage, a quantity. */
export function scale(a: Money, factor: string | number): Money {
  return a.times(new Decimal(factor)).toDecimalPlaces(SCALE) as Money;
}

/** Percentage of an amount. percent('12480.00', '30') -> 3744.0000 */
export function percent(a: Money, pct: string | number): Money {
  return scale(a, new Decimal(pct).dividedBy(100).toString());
}

export function cmp(a: Money, b: Money): number { return a.comparedTo(b); }
export function eq(a: Money, b: Money): boolean { return a.equals(b); }
export function gt(a: Money, b: Money): boolean { return a.greaterThan(b); }
export function gte(a: Money, b: Money): boolean { return a.greaterThanOrEqualTo(b); }
export function lt(a: Money, b: Money): boolean { return a.lessThan(b); }
export function lte(a: Money, b: Money): boolean { return a.lessThanOrEqualTo(b); }
export function isZero(a: Money): boolean { return a.isZero(); }
export function isNegative(a: Money): boolean { return a.isNegative(); }

/**
 * Split an amount across weights so the parts sum EXACTLY to the whole.
 * Largest-remainder: no cent is invented or lost.
 *
 * PY-12 — one payment proof allocated across several invoices. The sum must
 * reconcile or Accounts works around the system from day one.
 */
export function allocate(total: Money, weights: readonly Money[]): Money[] {
  if (weights.length === 0) throw new MoneyError('allocate requires at least one weight');
  if (weights.some(isNegative)) throw new MoneyError('allocate weights must not be negative');

  const weightTotal = sum(weights);
  if (isZero(weightTotal)) throw new MoneyError('allocate weights must not sum to zero');

  const unit = new Decimal(10).pow(-SCALE);
  const raw = weights.map((w) => total.times(w).dividedBy(weightTotal));
  const floored = raw.map((r) => r.toDecimalPlaces(SCALE, Decimal.ROUND_DOWN));

  let remainder = total.minus(floored.reduce((a, b) => a.plus(b), new Decimal(0)));
  const order = raw
    .map((r, i) => ({ i, frac: r.minus(floored[i]!) }))
    .sort((a, b) => b.frac.comparedTo(a.frac));

  const out = floored.slice();
  let k = 0;
  while (remainder.greaterThan(0) && k < order.length) {
    const idx = order[k]!.i;
    out[idx] = out[idx]!.plus(unit);
    remainder = remainder.minus(unit);
    k += 1;
  }
  return out as Money[];
}

/** Display only. Never feed the result back into arithmetic. */
export function format(m: Money, locale: string, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(m.toNumber());
}
